// src/components/GroupsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { db } from '../firebase';
import {
    collection, query, where, getDocs, orderBy,
    doc, addDoc, deleteDoc, writeBatch, Timestamp
} from 'firebase/firestore';

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faSpinner, faUserPlus, faUsers, faTimes } from '@fortawesome/free-solid-svg-icons';

// Import react-bootstrap components
import { Alert, Modal, Form, Button } from 'react-bootstrap';

// Import the Auth Context hook
import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';

import './GroupsPage.css';

// --- Main Component: GroupsPage ---
function GroupsPage() {
    const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

    // Local state
    const [groups, setGroups] = useState([]);
    const [groupMap, setGroupMap] = useState(new Map());
    const [membersMap, setMembersMap] = useState(new Map());
    const [allMembers, setAllMembers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [rolesByGroup, setRolesByGroup] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Delete loading states
    const [deletingGroupId, setDeletingGroupId] = useState(null);
    const [deletingRoleId, setDeletingRoleId] = useState(null);

    // Delete modal state
    const [deleteModal, setDeleteModal] = useState({ show: false, type: null, id: null, name: '', groupName: '' });

    // Modal States
    const [showAddRole, setShowAddRole] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // State for Add Role Modal Form
    const [selectedMemberId, setSelectedMemberId] = useState('');
    const [roleLabel, setRoleLabel] = useState('');
    const [isSubmittingRole, setIsSubmittingRole] = useState(false);
    const [modalError, setModalError] = useState('');


    // --- Fetch core data (Groups, Members, Roles) ---
    const fetchData = useCallback(async () => {
        if (!userOrgId) {
            if (!loadingAuth && currentUser) {
                 setError("Organization details missing from your account. Cannot load groups.");
            }
            setLoading(false);
            return;
        }
        setLoading(true); setError(null); let isMounted = true;
        try {
            const groupsQuery = query(collection(db, 'group'), where('orgId', '==', userOrgId), orderBy('groupName'));
            const membersQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId), orderBy('surname'), orderBy('name'));
            const rolesQuery = query(collection(db, 'role'), where('orgId', '==', userOrgId));

            const [groupsSnap, membersSnap, rolesSnap] = await Promise.all([ getDocs(groupsQuery), getDocs(membersQuery), getDocs(rolesQuery) ]);

            if (!isMounted) return;

            const fetchedGroups = groupsSnap.docs.map(d => ({ ...d.data(), id: d.id, name: d.data().groupName }));
            const newGroupMap = new Map(fetchedGroups.map(g => [g.id, g]));

            const fetchedMembers = membersSnap.docs.map(d => ({
                ...d.data(),
                id: d.id,
                name: d.data().name || '',
                surname: d.data().surname || '',
                familyId: d.data().familyId || null,
                photo: d.data().photo,
                imageUpdatedAt: d.data().imageUpdatedAt,
            }));
            const newMembersMap = new Map(fetchedMembers.map(m => [m.id, m]));

            const fetchedRoles = rolesSnap.docs.map(d => ({ ...d.data(), id: d.id }));

            setGroups(fetchedGroups); setGroupMap(newGroupMap); setMembersMap(newMembersMap);
            setAllMembers(fetchedMembers); setRoles(fetchedRoles); setError(null);
        } catch (err) { console.error("GroupsPage: Error fetching data", err); if (isMounted) { setError(err.message || "Failed to load group data."); } }
        finally { if (isMounted) { setLoading(false); } }
    }, [userOrgId, loadingAuth, currentUser]);

    useEffect(() => {
        if (!loadingAuth && userOrgId) { fetchData(); }
        else if (!loadingAuth && !userOrgId && currentUser) {
            setError("Organization details missing from your account. Cannot load groups.");
            setLoading(false);
        } else if (!loadingAuth && !currentUser) {
            setGroups([]); setGroupMap(new Map()); setMembersMap(new Map());
            setAllMembers([]); setRoles([]); setRolesByGroup({});
            setLoading(false); setError(null);
        }
    }, [loadingAuth, userOrgId, fetchData, currentUser]);

    // Effect to process roles and include member photo details
    useEffect(() => {
        if (loading || !roles || groupMap.size === 0 || membersMap.size === 0 || !userOrgId) {
            setRolesByGroup({});
            return;
        }
        const grouped = {}; groupMap.forEach((_, groupId) => { grouped[groupId] = []; });

        roles.forEach(role => {
            const matchedGroup = groupMap.get(role.groupId);
            const matchedMember = membersMap.get(role.memberId);

            if (matchedGroup && matchedMember && role.orgId === userOrgId && matchedGroup.orgId === userOrgId && matchedMember.orgId === userOrgId) {
                const nameToDisplay = role.memberName || `${matchedMember.name || ''} ${matchedMember.surname || ''}`.trim() || 'Unknown Member';
                const familyIdToUse = role.familyId || matchedMember.familyId || null;

                grouped[role.groupId].push({
                    roleId: role.id,
                    memberId: role.memberId,
                    groupId: role.groupId,
                    label: role.label || '',
                    name: nameToDisplay,
                    familyId: familyIdToUse,
                    photo: matchedMember.photo,
                    imageUpdatedAt: matchedMember.imageUpdatedAt,
                });
            }
        });
        Object.keys(grouped).forEach(groupId => grouped[groupId].sort((a, b) => a.name.localeCompare(b.name)));
        setRolesByGroup(grouped);
    }, [groupMap, membersMap, roles, loading, userOrgId]);


    // --- CRUD Handlers ---
    const openDeleteModal = (type, id, name, groupName = '') => {
        setDeleteModal({ show: true, type, id, name, groupName });
    };

    const closeDeleteModal = () => {
        if (!deletingGroupId && !deletingRoleId) {
            setDeleteModal({ show: false, type: null, id: null, name: '', groupName: '' });
        }
    };

    const handleConfirmDelete = useCallback(async () => {
        const { type, id, name } = deleteModal;
        setError(null);

        if (type === 'group') {
            if (!id || !userOrgId || !isAdmin) return;
            setDeletingGroupId(id);
            try {
                const rolesToDeleteQuery = query(collection(db, 'role'), where('orgId', '==', userOrgId), where('groupId', '==', id));
                const rolesToDeleteSnap = await getDocs(rolesToDeleteQuery);
                const batch = writeBatch(db);
                rolesToDeleteSnap.forEach(doc => batch.delete(doc.ref));
                batch.delete(doc(db, 'group', id));
                await batch.commit();
                setGroups(prev => prev.filter(g => g.id !== id));
                setRolesByGroup(prev => {
                    const newRoles = {...prev};
                    delete newRoles[id];
                    return newRoles;
                });
                setDeleteModal({ show: false, type: null, id: null, name: '', groupName: '' });
            } catch (err) { console.error("Error deleting group:", err); setError("Failed to delete group: " + err.message); }
            finally { setDeletingGroupId(null); }
        } else if (type === 'role') {
            if (!id || !userOrgId || !isAdmin) return;
            setDeletingRoleId(id);
            try {
                await deleteDoc(doc(db, 'role', id));
                setRoles(prevRoles => prevRoles.filter(r => r.id !== id));
                setDeleteModal({ show: false, type: null, id: null, name: '', groupName: '' });
            } catch (err) { console.error("Error deleting role:", err); setError("Failed to delete role: " + err.message); }
            finally { setDeletingRoleId(null); }
        }
    }, [deleteModal, userOrgId, isAdmin]);

    const handleAddRole = useCallback(async (groupId, memberId, label, familyId, memberName) => {
        if (!groupId || !memberId || !userOrgId || !isAdmin) {
            return Promise.reject(new Error("Cannot add role due to missing data or permissions."));
        }
        try {
            const groupData = groups.find(g => g.id === groupId);
            await addDoc(collection(db, 'role'), {
                groupId: groupId,
                memberId: memberId,
                familyId: familyId,
                memberName: memberName,
                label: label || "",
                orgId: userOrgId,
                groupName: groupData?.name || "",
                cDate: Timestamp.now()
            });
             return Promise.resolve();
        } catch (err) {
            console.error("Error adding role:", err);
            return Promise.reject(err);
        }
    }, [userOrgId, isAdmin, groups]);



    // --- Modal Handlers ---
    const handleOpenAddRoleModal = (group) => { setSelectedGroup(group); setSelectedMemberId(''); setRoleLabel(''); setModalError(''); setShowAddRole(true); };
    const handleCloseAddRoleModal = () => { setShowAddRole(false); setSelectedGroup(null); setModalError(''); setSelectedMemberId(''); setRoleLabel('');};
    const handleModalSubmit = async () => {
        setModalError('');
        if (!selectedMemberId) { setModalError("Please select a member to add."); return; }
        if (!selectedGroup) { setModalError("Selected group information is missing (internal error)."); return; }
        setIsSubmittingRole(true);

        const selectedMember = allMembers.find(m => m.id === selectedMemberId);
        let familyIdToAdd = null;
        let memberNameToAdd = '';

        if (selectedMember) {
            familyIdToAdd = selectedMember.familyId || null;
            memberNameToAdd = `${selectedMember.name || ''} ${selectedMember.surname || ''}`.trim();
        } else {
            setModalError("Could not find selected member details. Please try again.");
            setIsSubmittingRole(false);
            return;
        }
        if (!memberNameToAdd) {
             memberNameToAdd = 'Unknown Member';
        }

        try {
            await handleAddRole(selectedGroup.id, selectedMemberId, roleLabel, familyIdToAdd, memberNameToAdd);
            handleCloseAddRoleModal();
            fetchData();
        } catch (err) { setModalError(err.message || "Failed to add member to group."); }
        finally { setIsSubmittingRole(false); }
    };

    const availableMembersForDropdown = useMemo(() => {
        if (!selectedGroup || !rolesByGroup[selectedGroup.id] || !allMembers) { return allMembers || []; }
        const memberIdsInGroup = new Set(rolesByGroup[selectedGroup.id].map(role => role.memberId));
        return allMembers.filter(member => !memberIdsInGroup.has(member.id));
    }, [selectedGroup, rolesByGroup, allMembers]);


    // --- Render Logic ---

    // Loading state
    if (loadingAuth || loading) {
        return (
            <div className="groups-page">
                <div className="groups-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-group-card"></div>
                    <div className="skeleton-group-card"></div>
                    <div className="skeleton-group-card"></div>
                </div>
            </div>
        );
    }

    // Not logged in
    if (!currentUser) {
        return (
            <div className="groups-page">
                <div className="container mt-3">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view groups.
                    </Alert>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="groups-page">
                <div className="groups-header">
                    <div className="container">
                        <div className="groups-header-content">
                            <div>
                                <h1 className="groups-title">Groups</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        Error: {error}
                    </Alert>
                </div>
                {isAdmin && (
                    <Link to="/groups/add" className="fab-create-group">
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Create Group</span>
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div className="groups-page">
            {/* Page Header */}
            <div className="groups-header">
                <div className="container">
                    <div className="groups-header-content">
                        <div>
                            <h1 className="groups-title">Groups</h1>
                            {groups.length > 0 && (
                                <div className="groups-count">{groups.length} group{groups.length !== 1 ? 's' : ''}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Empty State */}
                {groups.length === 0 && (
                    <div className="groups-empty">
                        <div className="groups-empty-icon">
                            <FontAwesomeIcon icon={faUsers} />
                        </div>
                        <h3>No Groups Yet</h3>
                        <p>
                            {isAdmin
                                ? "Create your first group to organize members into teams, committees, or ministries."
                                : "No groups have been created for this organization yet."
                            }
                        </p>
                        {isAdmin && (
                            <Link to="/groups/add" className="groups-empty-cta">
                                <FontAwesomeIcon icon={faPlus} /> Create First Group
                            </Link>
                        )}
                    </div>
                )}

                {/* Group Cards */}
                {groups.map(group => {
                    const memberCount = rolesByGroup[group.id]?.length || 0;

                    return (
                        <div key={group.id} className="group-card" style={{ position: 'relative' }}>
                            {deletingGroupId === group.id && (
                                <div className="deleting-overlay">
                                    <FontAwesomeIcon icon={faSpinner} spin className="deleting-overlay-spinner" />
                                    <span className="deleting-overlay-text">Deleting group...</span>
                                </div>
                            )}
                            <div className="group-card-header">
                                <h2 className="group-name">
                                    {group.name}
                                    <span className="group-member-count">
                                        {memberCount} member{memberCount !== 1 ? 's' : ''}
                                    </span>
                                </h2>
                                {isAdmin && (
                                    <div className="group-actions">
                                        <button
                                            className="group-action-btn group-action-btn-add"
                                            onClick={() => handleOpenAddRoleModal(group)}
                                            title="Add Member to Group"
                                        >
                                            <FontAwesomeIcon icon={faUserPlus} />
                                            <span>Add Member</span>
                                        </button>
                                        <button
                                            className="group-action-btn group-action-btn-delete"
                                            onClick={() => openDeleteModal('group', group.id, group.name)}
                                            title="Delete Group"
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="group-card-body">
                                {memberCount === 0 ? (
                                    <p className="group-empty-message">No members in this group yet.</p>
                                ) : (
                                    <div className="group-members-grid">
                                        {rolesByGroup[group.id].map(role => {
                                            const photoCacheKey = role.imageUpdatedAt instanceof Timestamp
                                                ? role.imageUpdatedAt.toMillis().toString()
                                                : (role.imageUpdatedAt || role.memberId);

                                            return (
                                                <Link
                                                    key={role.roleId}
                                                    to={role.familyId
                                                        ? `/families/${role.familyId}/member/${role.memberId}`
                                                        : (isAdmin ? `/members/edit/${role.memberId}` : '#')
                                                    }
                                                    className="group-member-item"
                                                    style={{ position: 'relative' }}
                                                    onClick={(e) => { if (!isAdmin && !role.familyId) e.preventDefault();}}
                                                    title={role.familyId ? `View ${role.name}` : (isAdmin ? `Edit ${role.name}` : role.name)}
                                                >
                                                    {deletingRoleId === role.roleId && (
                                                        <div className="deleting-overlay deleting-overlay-small">
                                                            <FontAwesomeIcon icon={faSpinner} spin className="deleting-overlay-spinner" />
                                                        </div>
                                                    )}
                                                    <div className="group-member-photo">
                                                        <MemberPhotoAvatar
                                                            src={role.photo}
                                                            alt={role.name || 'Member'}
                                                            size={56}
                                                            cacheKey={photoCacheKey}
                                                        />
                                                        {isAdmin && (
                                                            <button
                                                                className="group-member-remove"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    openDeleteModal('role', role.roleId, role.name, group.name);
                                                                }}
                                                                title={`Remove ${role.name}`}
                                                            >
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <span className="group-member-name">{role.name}</span>
                                                    {role.label && (
                                                        <span className="group-member-role">{role.label}</span>
                                                    )}
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* FAB Button */}
            {isAdmin && (
                <Link to="/groups/add" className="fab-create-group">
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create Group</span>
                </Link>
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={deleteModal.show} onHide={closeDeleteModal} centered className="delete-confirm-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faTrashAlt} />
                        {deleteModal.type === 'group' ? 'Delete Group' : 'Remove Member'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="delete-modal-message">
                        {deleteModal.type === 'group'
                            ? <>Are you sure you want to delete the group <strong>"{deleteModal.name}"</strong>?</>
                            : <>Remove <strong>{deleteModal.name}</strong> from the group <strong>"{deleteModal.groupName}"</strong>?</>
                        }
                    </p>
                    {deleteModal.type === 'group' && (
                        <div className="delete-modal-warning">
                            This will also remove <strong>all member assignments</strong> from this group. This action cannot be undone.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={closeDeleteModal} disabled={!!deletingGroupId || !!deletingRoleId}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={!!deletingGroupId || !!deletingRoleId}>
                        {(deletingGroupId || deletingRoleId) ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                {deleteModal.type === 'group' ? 'Deleting...' : 'Removing...'}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                {deleteModal.type === 'group' ? 'Delete' : 'Remove'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Member Modal */}
            {selectedGroup && (
                <Modal show={showAddRole} onHide={handleCloseAddRoleModal} centered className="groups-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>Add Member to "{selectedGroup.name}"</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalError && <Alert variant="danger" className="mb-3">{modalError}</Alert>}
                        <Form onSubmit={(e) => { e.preventDefault(); handleModalSubmit();}}>
                            <Form.Group className="mb-3" controlId="selectMember">
                                <Form.Label>Select Member <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={selectedMemberId}
                                    onChange={(e) => { setSelectedMemberId(e.target.value); setModalError(''); }}
                                    required
                                    disabled={isSubmittingRole}
                                >
                                    <option value="" disabled>-- Select a Member --</option>
                                    {availableMembersForDropdown.length > 0 ? (
                                        availableMembersForDropdown
                                            .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`))
                                            .map(member => (
                                                <option key={member.id} value={member.id}>
                                                    {member.name} {member.surname}
                                                </option>
                                            ))
                                    ) : (
                                        <option value="" disabled>No available members</option>
                                    )}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="roleLabelInput">
                                <Form.Label>Role Label (Optional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="E.g., Chairperson, Tenor, Volunteer"
                                    value={roleLabel}
                                    onChange={(e) => setRoleLabel(e.target.value)}
                                    disabled={isSubmittingRole}
                                    maxLength={50}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseAddRoleModal} disabled={isSubmittingRole}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleModalSubmit} disabled={isSubmittingRole || !selectedMemberId}>
                            {isSubmittingRole ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Adding...
                                </>
                            ) : (
                                "Add Member"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}

export default GroupsPage;
